import React from 'react'
import { useHistory } from 'react-router-dom'

// Utils
import { getItem, removeItem } from 'utils/localStorage'

// Constants
import localStorageKeys from 'constants/localStorageKeys'
import { routes } from 'constants/routes'

// Components
import Button from 'components/Button'
import { useToast } from 'containers/ToastManager'
import {
  PrivateWorkspaceRoute,
  PrivateWorkspaceRouteExtraProps,
} from 'modules/workspace/containers'

// Local Components
import EmptyState from '../EmptyState'
import EmptyStateWithBanners from './EmptyStateWithBanners'

// GQL
import {
  GetWorkspacesDocument,
  useDeleteWorkspaceMutation,
  WorkspaceMinimalFragment,
} from 'gqlTypes'

interface ExpiredWorkspaceActionsProps {
  workspaceId: string
  canRenewSubscription: boolean
}

const ExpiredWorkspaceActions: React.FC<ExpiredWorkspaceActionsProps> = ({
  workspaceId,
  canRenewSubscription,
}) => {
  const history = useHistory()
  const { showToast } = useToast()

  const [deleteWorkspace, { loading }] = useDeleteWorkspaceMutation({
    variables: { workspaceId },
    onError: 'show-toast',
    onCompleted: data => {
      const { deletedWorkspace } = data.deleteWorkspace || {}

      // Remove last visited workspace from local storage
      const storedWorkspaceId = getItem(localStorageKeys.lastWorkspaceIdKey)

      if (storedWorkspaceId === deletedWorkspace?.identifier) {
        removeItem(localStorageKeys.lastWorkspaceIdKey)
      }

      showToast('Your workspace was successfully deleted.', 'positive')
      history.push(routes.ENTRY.create({}))
    },
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetWorkspacesDocument }],
  })

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => deleteWorkspace()}
        loading={loading}>
        Leave Workspace
      </Button>
      {canRenewSubscription ? (
        <Button
          variant="primary"
          onClick={() =>
            history.push(
              routes.WORKSPACE_SUBSCRIBE.create({
                workspaceId,
              })
            )
          }
          disabled={loading}>
          Renew Subscription
        </Button>
      ) : null}
    </>
  )
}

interface ShareEmptyStateProps {
  filter: 'LIBRARY' | 'NO_PROJECT' | 'SHARED_WITH_ME' | null
  search?: string
  workspace: WorkspaceMinimalFragment
  workspaceHasDocuments?: boolean | null
}

export const ShareEmptyState: React.FC<ShareEmptyStateProps> = ({
  filter,
  search,
  workspace,
  workspaceHasDocuments,
}) => {
  const isOwner = workspace?.userIsOwner
  const isPersonalWorkspace = workspace?.type === 'PERSONAL'

  return (
    <PrivateWorkspaceRoute
      component={({ workspace }: PrivateWorkspaceRouteExtraProps) => {
        const { identifier, status, userCanEdit, userRole } = workspace

        if (status === 'INACTIVE' && [null, 'NO_PROJECT'].includes(filter)) {
          return (
            <EmptyState
              icon="workspace"
              title="Cannot view this Workspace"
              description={
                <>
                  You cannot access the document because the Workspace is
                  currently inactive.
                  <br />
                  Renew your subscription to activate the Workspace again.
                  <br />
                </>
              }
              callToAction={
                <ExpiredWorkspaceActions
                  workspaceId={identifier}
                  canRenewSubscription={
                    userRole === 'ADMIN' || userRole === 'FINANCE'
                  }
                />
              }
            />
          )
        }

        if (filter === null) {
          if ((!isOwner || isPersonalWorkspace) && !workspaceHasDocuments) {
            return <EmptyStateWithBanners workspace={workspace} />
          } else {
            return (
              <EmptyState
                search={search}
                title="Your Workspace hasn’t created anything yet"
                description="Create and upload a document in Sketch to see it here."
                icon="document"
              />
            )
          }
        }

        if (userCanEdit && filter === 'NO_PROJECT') {
          return (
            <EmptyState
              search={search}
              title="Nothing to see here"
              description="There are no documents to show here."
              icon="document"
            />
          )
        }

        if (userRole === 'GUEST' && filter === 'LIBRARY') {
          return (
            <EmptyState
              search={search}
              title="No Libraries available to view yet"
              description="Libraries shared with you in this Workspace will appear here."
              icon="link"
            />
          )
        }

        if (filter === 'LIBRARY') {
          return (
            <EmptyState
              search={search}
              title="Your Workspace hasn’t added any Libraries yet"
              description="Any documents with “Use as Library” checked in Document Settings will appear here."
              icon="link"
            />
          )
        }

        if (filter === 'SHARED_WITH_ME') {
          return (
            <EmptyState
              search={search}
              title="Nothing shared with you"
              description="These are documents which have been directly shared with you."
              icon="person-circle"
            />
          )
        }

        return (
          <EmptyState
            search={search}
            title="Your Workspace hasn’t created anything yet"
            description="Only Editors can create documents."
            icon="document"
          />
        )
      }}
    />
  )
}

export default ShareEmptyState
